/**
 * Site form validation utilities
 *
 * A site accepts reports from a list of host patterns (task 303). The form
 * edits that list as rows: a host, and whether to also accept every subdomain
 * of its registrable domain, which adds `*.<domain>` beside it. The grammar is
 * `parseHostPattern` from `@headerhawk/core/hosts`, the same function the API
 * validates with, so an error shown here is the error the API would give.
 */

import { ApiError } from "@/lib/api/client";
import {
  MAX_HOSTS_PER_SITE,
  parseHostPattern,
  registrableDomain,
  subdomainWildcardFor,
} from "@headerhawk/core/hosts";

/** One row of the host list editor. */
export interface HostRow {
  host: string;
  /** Also accept `*.<registrable domain of host>`. */
  includeSubdomains: boolean;
}

export interface SiteFormData {
  name: string;
  /** The normalised patterns to send as `hosts`. */
  hosts: string[];
}

export interface SiteValidationErrors {
  name?: string;
  /** An error about the list as a whole: empty, or too long. */
  hosts?: string;
  /** Errors about single rows, by row index. */
  hostRows?: Record<number, string>;
}

/**
 * Normalize a host input:
 * - Strip protocol (http://, https://)
 * - Strip paths and query strings
 * - Convert to lowercase
 *
 * Only what a person pastes from an address bar; a port is left in so the
 * grammar can say why it is refused.
 */
export function normalizeHostInput(input: string): string {
  return input
    .trim()
    .replace(/^https?:\/\//i, "")
    .replace(/[/?#].*$/, "")
    .toLowerCase()
    .trim();
}

/**
 * The wildcard the "all subdomains" checkbox would add for a host, and the
 * domain to name in its label, or undefined when it does not apply: an
 * unparseable host, a wildcard already, an IP address, `localhost`.
 */
export function subdomainOptionFor(
  input: string,
): { wildcard: string; domain: string } | undefined {
  const parsed = parseHostPattern(normalizeHostInput(input));
  if (!parsed.ok || parsed.value.kind !== "exact") return undefined;
  const wildcard = subdomainWildcardFor(parsed.value.host);
  const domain = registrableDomain(parsed.value.host);
  return wildcard && domain ? { wildcard, domain } : undefined;
}

export interface RowPatterns {
  patterns: string[];
  /** For each pattern, the row it came from, so an API error finds its row. */
  rowOf: number[];
}

/**
 * The patterns a set of rows stands for, in row order.
 *
 * A wildcard two rows both imply (`www.example.com` and `shop.example.com`,
 * each with subdomains ticked), or that a row already lists on its own, is
 * sent once: the checkbox asks for it to be covered, and it is. A host typed
 * twice is sent twice, so the duplicate is reported against the row that
 * repeats it.
 */
export function rowsToPatterns(rows: readonly HostRow[]): RowPatterns {
  const patterns: string[] = [];
  const rowOf: number[] = [];
  const implied = new Set<string>();
  for (const row of rows) {
    const parsed = parseHostPattern(normalizeHostInput(row.host));
    if (parsed.ok && parsed.value.kind === "wildcard") {
      implied.add(parsed.value.pattern);
    }
  }

  rows.forEach((row, index) => {
    const host = normalizeHostInput(row.host);
    if (!host) return;
    const parsed = parseHostPattern(host);
    patterns.push(parsed.ok ? parsed.value.pattern : host);
    rowOf.push(index);

    if (!row.includeSubdomains) return;
    const option = subdomainOptionFor(host);
    if (!option || implied.has(option.wildcard)) return;
    implied.add(option.wildcard);
    patterns.push(option.wildcard);
    rowOf.push(index);
  });

  return { patterns, rowOf };
}

/**
 * Rows for a stored list: each exact host, with its subdomain box ticked when
 * the list also holds that host's wildcard. A wildcard no exact host claims
 * gets a row of its own.
 */
export function patternsToRows(patterns: readonly string[]): HostRow[] {
  const remaining = new Set(patterns);
  const rows: HostRow[] = [];

  for (const pattern of patterns) {
    if (pattern.startsWith("*.") || !remaining.has(pattern)) continue;
    remaining.delete(pattern);
    const option = subdomainOptionFor(pattern);
    const claimed = option !== undefined && remaining.has(option.wildcard);
    if (claimed) remaining.delete(option.wildcard);
    rows.push({ host: pattern, includeSubdomains: claimed });
  }
  for (const pattern of patterns) {
    if (remaining.has(pattern)) {
      rows.push({ host: pattern, includeSubdomains: false });
    }
  }
  return rows;
}

/** Validate the host rows, as the API would. */
export function validateHostRows(
  rows: readonly HostRow[],
): Pick<SiteValidationErrors, "hosts" | "hostRows"> {
  const hostRows: Record<number, string> = {};
  const seen = new Set<string>();
  let filled = 0;

  rows.forEach((row, index) => {
    const host = normalizeHostInput(row.host);
    if (!host) return;
    filled += 1;
    const parsed = parseHostPattern(host);
    if (!parsed.ok) {
      hostRows[index] = parsed.error.message;
    } else if (seen.has(parsed.value.pattern)) {
      hostRows[index] = `${parsed.value.pattern} is already in the list`;
    } else {
      seen.add(parsed.value.pattern);
    }
  });

  const errors: Pick<SiteValidationErrors, "hosts" | "hostRows"> = {};
  if (Object.keys(hostRows).length > 0) errors.hostRows = hostRows;
  if (filled === 0) {
    errors.hosts = "Enter the host your pages are served from";
  } else if (rowsToPatterns(rows).patterns.length > MAX_HOSTS_PER_SITE) {
    errors.hosts = `A site can have at most ${MAX_HOSTS_PER_SITE} hosts`;
  }
  return errors;
}

/**
 * Validate site form data
 * Returns errors object with field-specific error messages
 */
export function validateSiteForm(data: {
  name: string;
  hosts: readonly HostRow[];
}): SiteValidationErrors {
  const errors: SiteValidationErrors = {};

  const trimmedName = data.name.trim();
  if (!trimmedName) {
    errors.name = "Site name is required";
  } else if (trimmedName.length > 100) {
    errors.name = "Site name must be 100 characters or less";
  }

  return { ...errors, ...validateHostRows(data.hosts) };
}

/**
 * Check if validation errors object has any errors
 */
export function hasErrors(errors: SiteValidationErrors): boolean {
  return Object.keys(errors).length > 0;
}

/** One refused entry in an API `400`, as `details.hosts` carries it. */
export interface HostRejection {
  index: number;
  message: string;
}

/**
 * Place the API's per-pattern errors on the rows that produced them.
 */
export function rowErrorsFromRejections(
  rejections: readonly HostRejection[],
  rowOf: readonly number[],
): Record<number, string> {
  const errors: Record<number, string> = {};
  for (const rejection of rejections) {
    const row = rowOf[rejection.index];
    if (row !== undefined && errors[row] === undefined) {
      errors[row] = rejection.message;
    }
  }
  return errors;
}

/**
 * The per-entry rejections a refused host list carries in its `400`, or
 * undefined for any other failure.
 */
export function hostRejectionsOf(error: unknown): HostRejection[] | undefined {
  if (!(error instanceof ApiError) || error.status !== 400) return undefined;
  const hosts = error.details?.["hosts"];
  if (!Array.isArray(hosts)) return undefined;
  const rejections = hosts.filter(
    (entry): entry is HostRejection =>
      typeof entry === "object" &&
      entry !== null &&
      typeof (entry as HostRejection).index === "number" &&
      typeof (entry as HostRejection).message === "string",
  );
  return rejections.length > 0 ? rejections : undefined;
}
